import {
  AutocompleteArrayInput,
  AutocompleteInput,
  Button,
  FormDataConsumer,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  TextInput,
  useNotify,
} from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useField } from "react-final-form";
import { cloneDeep, findIndex } from "lodash";
import SanitizedGrid from "../../../components/SanitizedGrid";
import AssignedSubcontractorInput from "./assignedSubcontractors/AssignedSubcontractorInput";
import { Form } from "react-final-form";
import { GridinRowsWithSpace } from "utils/style";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../components/layout/Menu";
import { changeSelectedRoute } from "../../../redux/navmode.action";
import EditableRouteLocationComponent from "./EditableRouteLocationComponent";
import BackButton from "./BackButton";
import Stack from "../../../components/Stack";
import AddLocationButton from "./AddLocationButton";
import CustomTimeRangeInput from "../../../components/CustomTimeRangeInput";
import Alert from "@material-ui/lab/Alert";
import {
  calculateDurationInSeconds,
  calculateDurationInString,
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import {
  isAssignedSubcontractorRequiredValidation,
  isValidTimeRangeValidation,
} from "utils/validation";

interface DetailOptimiseRouteComponentProps {
  setIsOpenDetailRoute: any;
  mode: "create" | "update";
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  routeNameField: {
    "& .MuiFormHelperText-root": {
      display: "none",
    },
  },
});

const DetailOptimiseRouteComponent: React.FC<
  DetailOptimiseRouteComponentProps
> = ({ setIsOpenDetailRoute, mode }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const notify = useNotify();
  const routesField = useField("routes");
  const busScheduleField = useField("busSchedule");
  const schoolField = useField("school");
  const formRoutesChangedField = useField("formRoutesChanged");
  const desiredRouteDurationField = useField("desiredRouteDuration");
  const maxStopTimeField = useField("maxStopTime");

  const [selectedData, setSelectedData] = React.useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const selectedRouteState = useSelector(
    (state: AppState) => state.navMode.route,
  );

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveAdjustRoute = async (values: any) => {
    try {
      setIsLoading(true);
      const routes = cloneDeep(routesField.input.value);

      // check if have duplicate route name
      const duplicateRouteName = routes.find(
        (route: any) =>
          route.routeName === values.routeName && route.id !== selectedData.id,
      );

      if (duplicateRouteName) {
        notify("Route name already exists", "error");
        setIsLoading(false);
        return;
      }

      for (const movedLocation of values.routeLocationsMoved) {
        const routeIndex = findIndex(routes, [
          "routeName",
          movedLocation.routeName,
        ]);

        if (routeIndex !== -1) {
          const route = routes[routeIndex];
          route.routeGeometries = [];

          const routeLocationWithoutSchool =
            removeSchoolLocationFromRouteLocation(
              [
                route.routeLocations[0],
                movedLocation.location,
                ...route.routeLocations.slice(1),
              ],
              busScheduleField.input.value.type,
            );

          const optimisedRouteLocations = await reOptimiseRouteLocation(
            schoolField.input.value,
            busScheduleField.input.value,
            maxStopTimeField.input.value,
            routeLocationWithoutSchool,
          );

          route.routeLocations = optimisedRouteLocations;
        }
      }

      const selectedRouteIndex = findIndex(routes, ["id", selectedData.id]);

      if (selectedRouteIndex !== -1) {
        routes[selectedRouteIndex] = {
          ...routes[selectedRouteIndex],
          ...values,
        };
      } else {
        routes.push(values);
      }

      routesField.input.onChange(routes);
      formRoutesChangedField.input.onChange(true);

      setIsLoading(false);
      setIsOpenDetailRoute(false);
      setSelectedData(null);
      dispatch(changeSelectedRoute(null));
    } catch (error) {
      setIsLoading(false);
      notify(`Failed to save the route, error:${error}`, error);
    }
  };

  useEffect(() => {
    if (selectedData === null) {
      setSelectedData({ ...selectedRouteState, routeLocationsMoved: [] });
    }
  }, [selectedRouteState]);

  return (
    <>
      {selectedData ? (
        <Form
          initialValues={selectedData}
          onSubmit={(values) => {
            handleSaveAdjustRoute(values);
          }}
        >
          {({ handleSubmit }) => (
            <SanitizedGrid container spacing={2}>
              <Grid item xs={12} style={GridinRowsWithSpace}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <BackButton setIsOpenDetailRoute={setIsOpenDetailRoute} />

                  <Grid item xs={12}>
                    <TextInput
                      className={classes.routeNameField}
                      source="routeName"
                      label="Route Name"
                      validate={required()}
                    />
                  </Grid>
                </div>

                <FormDataConsumer>
                  {({ formData }) => {
                    const isDisabled = !(
                      formData.routeName &&
                      formData.startTime &&
                      formData.endTime &&
                      formData.routeLocations.length > 0 &&
                      isAssignedSubcontractorRequiredValidation(formData) &&
                      isValidTimeRangeValidation(
                        formData.startTime,
                        formData.endTime,
                      )
                    );

                    return (
                      <Button
                        style={{ marginRight: "17px" }}
                        onClick={handleOpenDialog}
                        color="primary"
                        label="Save Changes"
                        disabled={isDisabled}
                      />
                    );
                  }}
                </FormDataConsumer>
              </Grid>

              <Grid item xs={12}>
                <Divider style={{ margin: "10px 17px 0px 17px" }} />
              </Grid>

              <Grid item md={6} xs={12} style={{ padding: "24px" }}>
                <SanitizedGrid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Operation Detail
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormDataConsumer>
                      {({ formData }) =>
                        calculateDurationInSeconds(
                          formData.startTime,
                          formData.endTime,
                        ) >
                          desiredRouteDurationField.input.value * 60 && (
                          <Alert
                            severity="warning"
                            style={{ marginBottom: "10px" }}
                          >
                            Please note that the approximate time exceeds{" "}
                            <strong>
                              the Desired Route Duration (
                              {desiredRouteDurationField.input.value} minutes)
                            </strong>
                            . Ops may choose to keep the current timing or make
                            adjustments (e.g., revising stops, adding or
                            removing stops) to better align with the target
                            duration.
                          </Alert>
                        )
                      }
                    </FormDataConsumer>

                    <CustomTimeRangeInput
                      fromSource="startTime"
                      fromLabel="Start Time *"
                      toSource="endTime"
                      toLabel="End Time *"
                    />

                    <FormDataConsumer>
                      {({ formData }) => (
                        <Alert severity="info" style={{ marginTop: "10px" }}>
                          <strong>Start Time</strong> and{" "}
                          <strong>End Time</strong> are approximate and are
                          calculated based on the planned route.
                          <br />
                          <br />
                          <strong>
                            Total duration:{" "}
                            {calculateDurationInString(
                              formData.startTime,
                              formData.endTime,
                            )}
                          </strong>
                        </Alert>
                      )}
                    </FormDataConsumer>
                  </Grid>

                  <Grid item xs={12}>
                    <TextInput source="busBayNumber" label="Bus Bay Number" />
                  </Grid>

                  <Grid item xs={12}>
                    <ReferenceInput
                      source="driverId"
                      label="Driver"
                      reference="drivers"
                      filterToQuery={(searchText) => {
                        if (searchText !== "") return { search: searchText };
                      }}
                      allowEmpty
                    >
                      <AutocompleteInput
                        optionText={(choice: any) =>
                          `${choice?.fullName} (${choice?.contactNumber})`
                        }
                      />
                    </ReferenceInput>
                  </Grid>
                  <Grid item xs={12}>
                    <ReferenceArrayInput
                      source="backupDriverIds"
                      label="Backup Drivers"
                      reference="drivers"
                      filterToQuery={(searchText: any) => {
                        if (searchText !== "") return { search: searchText };
                      }}
                    >
                      <AutocompleteArrayInput
                        optionText={(choice: any) =>
                          `${choice?.fullName} (${choice?.contactNumber})`
                        }
                      />
                    </ReferenceArrayInput>
                  </Grid>

                  <Grid item xs={12}>
                    <ReferenceInput
                      source="busAssistantId"
                      label="Bus Assistant"
                      reference="busAssistants"
                      filterToQuery={(searchText) => {
                        if (searchText !== "") return { search: searchText };
                      }}
                      allowEmpty
                    >
                      <AutocompleteInput
                        optionText={(choice: any) =>
                          `${choice?.fullName} (${choice?.contactNumber})`
                        }
                      />
                    </ReferenceInput>
                  </Grid>
                  <Grid item xs={12}>
                    <ReferenceArrayInput
                      source="backupBusAssistantIds"
                      label="Backup Bus Assistants"
                      reference="busAssistants"
                      filterToQuery={(searchText: any) => {
                        if (searchText !== "") return { search: searchText };
                      }}
                    >
                      <AutocompleteArrayInput
                        optionText={(choice: any) =>
                          `${choice?.fullName} (${choice?.contactNumber})`
                        }
                      />
                    </ReferenceArrayInput>
                  </Grid>

                  <Grid item xs={12}>
                    <ReferenceInput
                      source="vehicleId"
                      label="Vehicle"
                      reference="vehicles"
                      filterToQuery={(searchText) => {
                        if (searchText !== "") return { search: searchText };
                      }}
                      allowEmpty
                    >
                      <AutocompleteInput
                        optionText={(record) =>
                          `${record?.plateNumber} - ${
                            record?.seatCapacity
                              ? `${record?.seatCapacity} seat(s)`
                              : `No seat capacity info available`
                          }`
                        }
                        emptyText="-"
                      />
                    </ReferenceInput>
                  </Grid>
                  <Grid item xs={12}>
                    <ReferenceArrayInput
                      source="backupVehicleIds"
                      label="Backup Vehicles"
                      reference="vehicles"
                      filterToQuery={(searchText: any) => {
                        if (searchText !== "") return { search: searchText };
                      }}
                    >
                      <AutocompleteArrayInput optionText="plateNumber" />
                    </ReferenceArrayInput>
                  </Grid>

                  <AssignedSubcontractorInput
                    source="assignedSubcontractor"
                    mode={mode}
                  />
                </SanitizedGrid>
              </Grid>
              <Grid item md={6} xs={12} style={{ padding: "24px" }}>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    style={{ marginBottom: "20px" }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Locations and Students Detail
                    </Typography>

                    <AddLocationButton
                      busSchedule={busScheduleField.input.value}
                      school={schoolField.input.value}
                      maxStopTime={maxStopTimeField.input.value}
                      routes={routesField.input.value}
                    />
                  </Stack>

                  <Divider />
                </Grid>

                <EditableRouteLocationComponent
                  busSchedule={busScheduleField.input.value}
                  school={schoolField.input.value}
                  routesData={routesField.input.value}
                  maxStopTime={maxStopTimeField.input.value}
                />
              </Grid>

              <Dialog
                open={open}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                className={classes.dialog}
                maxWidth="md"
              >
                <DialogTitle id="alert-dialog-title">Save Changes</DialogTitle>
                <DialogContent>
                  <DialogContentText color="initial">
                    Are you sure you want to save changes to this route?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    label="Back"
                  />
                  <Button
                    onClick={handleSubmit}
                    color="primary"
                    label="Save"
                    startIcon={
                      isLoading && (
                        <CircularProgress
                          size={20}
                          style={{ color: "#FFFFFF" }}
                        />
                      )
                    }
                  />
                </DialogActions>
              </Dialog>
            </SanitizedGrid>
          )}
        </Form>
      ) : (
        <></>
      )}
    </>
  );
};

export default DetailOptimiseRouteComponent;
